import { parseMarketOutcomes, parseOutcomePrices } from '@/utils/market-outcomes';
import { resolveMediaUrl } from '@/utils/resolve-media-url';
import { resolveMarketDetailRouteId } from '@/utils/market-detail-map';
import { resolveCollectionSlug } from '@/domain/market/collection-slug.js';
import { resolveChannelMarketDetailRoute } from '@/utils/channel-market-detail-route.js';

const ACCENTS = ['#0f172a', '#2563eb', '#22c55e', '#f7931a', '#7c3aed', '#627eea'];

function pickAccent(seed, index) {
  const str = String(seed ?? index);
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return ACCENTS[Math.abs(hash) % ACCENTS.length];
}

function thumbInitial(title) {
  const match = String(title || '').match(/[\p{L}\p{N}]/u);
  return (match?.[0] || '?').toUpperCase();
}

function formatCompactNum(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return '0';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return String(Math.round(num));
}

function formatVolumeLabel(market) {
  const raw = market?.volume ?? market?.liquidity ?? market?.volume24hr ?? 0;
  const num = Number(raw);
  if (Number.isFinite(num) && num > 0) return `$${formatCompactNum(num)}`;
  const text = String(raw ?? '').trim();
  return text || '—';
}

function parseSearchOptions(market) {
  const labels = parseMarketOutcomes(market?.outcomes);
  const prices = parseOutcomePrices(market?.outcomePrices);
  if (!labels.length) return [];
  return labels.slice(0, 4).map((label, i) => {
    const p = prices[i];
    let percent = Number.isFinite(p) ? Math.round(p <= 1 ? p * 100 : p) : 0;
    if (!percent) percent = Math.round(100 / labels.length);
    const text = String(label).trim();
    return { key: `${i}-${text}`, label: text, percent };
  });
}

function pickPrimaryOption(options) {
  if (!options.length) return { label: '—', percent: 50 };
  return options.reduce((best, opt) => (opt.percent > best.percent ? opt : best), options[0]);
}

function resolveSearchCategory(category) {
  const c = String(category || '')
    .trim()
    .toLowerCase();
  if (c.includes('crypto')) return 'crypto';
  if (c.includes('sport')) return 'sports';
  return 'markets';
}

function buildSearchTags(market, categoryKey) {
  const tags = [{ key: 'market', label: 'Market', tone: 'purple' }];
  const cat = String(market?.category || '').trim();
  if (cat) tags.push({ key: categoryKey, label: cat, tone: 'gray' });
  return tags;
}

/** 将 page-with-users 市场项映射为搜索 UI 结构 */
export function mapSearchMarket(market, index = 0) {
  const title = String(market?.question || '').trim() || '—';
  const options = parseSearchOptions(market);
  const primaryOption = pickPrimaryOption(options);
  const category = resolveSearchCategory(market?.category);
  const marketId = String(market?.id ?? '').trim();
  const rawCollId = String(market?.collectionId ?? market?.collId ?? '').trim();
  const slug = resolveCollectionSlug(market);
  const collId = rawCollId || resolveMarketDetailRouteId(market) || marketId;

  const route = resolveChannelMarketDetailRoute({
    collectionId: collId,
    marketId,
    slug,
    category: market?.category,
    homeTeamName: market?.homeTeamName ?? market?.home_team_name,
    awayTeamName: market?.awayTeamName ?? market?.away_team_name,
    contestId: market?.contestId ?? market?.contest_id,
    markets: market?.markets,
  });

  return {
    id: marketId || String(index),
    title,
    initial: thumbInitial(title),
    accent: pickAccent(title, index),
    category,
    traders: Number(market?.volume24hr ?? 0) || 0,
    volume: formatVolumeLabel(market),
    primaryOption,
    tags: buildSearchTags(market, category),
    options,
    imageUrl: resolveMediaUrl(String(market?.image || market?.icon || '').trim()),
    route,
  };
}

/** 将 page-with-users 用户项映射为搜索 UI 结构 */
export function mapSearchUser(user, index = 0) {
  const nickname = String(user?.nickname || user?.nickName || '').trim();
  const userId = String(user?.id ?? user?.userId ?? '').trim();
  const username = nickname.replace(/^@/, '') || userId || 'user';
  const name = nickname.startsWith('@') ? nickname : `@${username}`;

  return {
    id: userId,
    name,
    followers: Number(user?.followers ?? 0) || 0,
    winRate: Number(user?.winRate ?? 0) || 0,
    initial: thumbInitial(nickname || username),
    accent: pickAccent(name, index),
    avatar: resolveMediaUrl(String(user?.avatar || '').trim()),
  };
}

/** 解析 page-with-users 响应 */
export function normalizeMarketSearchResponse(data, startIndex = 0) {
  const rawMarkets = Array.isArray(data?.markets) ? data.markets : [];
  const rawUsers = Array.isArray(data?.users) ? data.users : [];
  const markets = rawMarkets.map((market, i) => mapSearchMarket(market, startIndex + i));
  const users = rawUsers.map((user, i) => mapSearchUser(user, i));
  return {
    markets,
    users,
    total: Number(data?.total ?? markets.length) || 0,
  };
}
